import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Check, Star, Clock } from 'lucide-react';

const PRICING = [
  {
    name: 'Swing Trading',
    subtitle: 'Multi-day position framework',
    price: 247,
    slug: 'swing-trading',
    accent: 'sky',
    badge: 'Swing Strategy',
    comingSoon: false,
    features: [
      { text: 'Multi-day swing entry framework', included: true },
      { text: 'Daily & H4 timeframe analysis', included: true },
      { text: 'Trend continuation setups', included: true },
      { text: 'Risk-to-reward optimisation', included: true },
      { text: 'Complete swing trading manual (PDF)', included: true },
    ],
  },
  {
    name: 'Position Trading',
    subtitle: 'Weeks-to-months positions',
    price: 247,
    slug: 'position-trading',
    accent: 'emerald',
    badge: 'Long-Horizon Strategy',
    comingSoon: false,
    features: [
      { text: 'Weekly & Monthly timeframe strategy', included: true },
      { text: 'Macro + fundamentals filtering', included: true },
      { text: 'Pyramiding & scaling techniques', included: true },
      { text: 'Long-horizon trade management', included: true },
      { text: 'Complete position trading manual (PDF)', included: true },
    ],
  },
  {
    name: 'Pro Trader Bundle',
    subtitle: 'Swing + Position + 9-Step Pro + Bow-Tie Scalping',
    price: 897, originalPrice: 1288,
    slug: 'pro-trader-bundle',
    accent: 'amber',
    badge: 'Best Value — Save $391',
    popular: true,
    comingSoon: false,
    features: [
      { text: 'Swing Trading (Advanced)', included: true },
      { text: 'Position Trading (Advanced)', included: true },
      { text: '9-Step Pro System (Intermediate)', included: true },
      { text: 'Bow-Tie Scalping System (Intermediate)', included: true },
      { text: 'All EAs, indicators & manuals included', included: true },
      { text: 'Priority email support', included: true },
      { text: 'Lifetime license for everything', included: true },
    ],
  },
];

export default function AdvancedPage() {
  const { addItem, items } = useCart();

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="advanced-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-amber-400/[0.03] blur-[150px]" />
        <div className="relative mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400/80 mb-3">Level 3 — Master the Markets</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
            Advanced <span className="text-amber-400">Courses</span>
          </h1>
          <p className="mt-5 text-sm sm:text-base text-white/40 max-w-2xl mx-auto leading-relaxed">
            For traders ready to expand beyond day trading and scalping. Master longer-horizon swing and position trading — or get the entire Hettie Nel methodology in one bundle.
          </p>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-white/30">
            <span>Swing &amp; Position</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>Pro Trader Bundle</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>30-Day Money Back</span>
          </div>
        </div>
      </section>

      {/* Prerequisite Notice */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8" data-testid="advanced-prerequisite">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl bg-amber-500/[0.05] border border-amber-500/20 p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
              <span className="text-amber-500 font-bold text-lg">!</span>
            </div>
            <div>
              <p className="text-sm font-bold text-white mb-1">Prerequisite: Intermediate Courses</p>
              <p className="text-xs text-slate-400 leading-relaxed">
                These advanced courses build on the <a href="/intermediate" className="text-amber-500 underline hover:text-amber-400">9-Step Pro System and Bow-Tie Scalping</a>. Make sure you&apos;re comfortable with both before scaling up.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="pricing-section">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Advanced Courses</h2>
          <p className="text-sm text-white/40 mb-10 text-center">One-time payment. Lifetime access. 30-day money-back guarantee.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRICING.map(plan => {
              const inCart = items.some(i => i.slug === plan.slug);
              const accentColors = {
                amber: { border: 'border-amber-400/30', bg: 'bg-amber-400/[0.03]', ring: 'ring-amber-400/10', text: 'text-amber-400', btn: 'bg-amber-400 text-black hover:bg-amber-300', check: 'text-amber-400' },
                sky:   { border: 'border-sky-400/30',   bg: 'bg-sky-400/[0.03]',   ring: 'ring-sky-400/10',   text: 'text-sky-400',   btn: 'bg-sky-400 text-black hover:bg-sky-300',   check: 'text-sky-400'   },
                emerald: { border: 'border-emerald-400/30', bg: 'bg-emerald-400/[0.03]', ring: 'ring-emerald-400/10', text: 'text-emerald-400', btn: 'bg-emerald-400 text-black hover:bg-emerald-300', check: 'text-emerald-400' },
              };
              const c = accentColors[plan.accent] || accentColors.amber;
              return (
                <div key={plan.slug} className={`rounded-xl border p-6 flex flex-col ${c.border} ${c.bg} ring-1 ${c.ring}`} data-testid={`pricing-${plan.slug}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider ${c.text} mb-3`}>{plan.badge}</p>
                  <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                  <p className="text-xs text-white/40 mt-0.5">{plan.subtitle}</p>
                  <div className="mt-4 flex items-baseline gap-2">
                    {plan.price ? (
                      <>
                        <span className="text-3xl font-bold text-white">${plan.price}</span>
                        {plan.originalPrice && <span className="text-sm text-white/30 line-through">${plan.originalPrice}</span>}
                      </>
                    ) : (
                      <span className="text-2xl font-bold text-white/40">Pricing TBA</span>
                    )}
                  </div>
                  <p className="text-[10px] text-white/30 mt-1">{plan.price ? 'One-time payment' : 'Coming soon'}</p>
                  {plan.comingSoon ? (
                    <div className={`mt-5 w-full py-2.5 rounded-lg text-sm font-semibold text-center bg-white/5 text-white/40 inline-flex items-center justify-center gap-2`} data-testid={`pricing-soon-${plan.slug}`}>
                      <Clock size={14} /> Coming Soon
                    </div>
                  ) : (
                    <button
                      onClick={() => addItem({ slug: plan.slug, name: plan.name, price: plan.price, image_url: '', platform: 'MT5' })}
                      disabled={inCart}
                      className={`mt-5 w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${inCart ? 'bg-white/5 text-white/30' : c.btn}`}
                      data-testid={`pricing-add-${plan.slug}`}
                    >
                      {inCart ? 'In Cart' : 'Get Started'}
                    </button>
                  )}
                  <ul className="mt-5 space-y-2 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-white/50">
                        <Check size={13} className={`${c.check} mt-0.5 shrink-0`} /> {f.text}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04]" data-testid="advanced-trust">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-bold text-white mb-4">Why Traders Trust Hettie Nel</h2>
          <ul className="space-y-3 text-sm text-white/50">
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 20+ years of active forex trading and one-on-one training</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 4.9/5 rating on Forex Peace Army (71+ verified reviews)</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> 900+ students trained through personal sessions</li>
            <li className="flex items-start gap-2"><Star size={14} className="text-amber-400 mt-0.5 shrink-0" /> Strategies tested through bull markets, bear markets, crashes, and recoveries</li>
          </ul>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 border-t border-white/[0.04] text-center" data-testid="advanced-cta">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-bold text-white">Trade with Discipline. Trade with a System.</h2>
          <p className="mt-3 text-sm text-white/40">The Pro Trader Bundle gives you every course Hettie teaches — day trading, scalping, swing and position — in one lifetime-access package.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/products" className="px-6 py-3 rounded-lg bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300 transition-colors" data-testid="advanced-cta-robots">
              Browse Robots
            </Link>
            <Link to="/contact" className="px-6 py-3 rounded-lg border border-white/10 text-white/60 text-sm hover:border-white/20 hover:text-white transition-colors" data-testid="advanced-cta-contact">
              Contact Hettie
            </Link>
          </div>
          <p className="mt-4 text-[10px] text-white/20">Secure payment via Stripe — instant download after purchase</p>
        </div>
      </section>
    </div>
  );
}
